/**
 * /verb-conjugation — спряжение глаголов восьми языков на ОДНОЙ странице.
 *
 * Язык глагола выбирается внутри инструмента и не равен языку страницы.
 * Модуль правил языка (lang/<code>.js) грузится динамическим import() только
 * при выборе, данные — загрузчиком с прогрессом по байтам (engine/loader.js).
 * Пока новый язык грузится, прежняя таблица остаётся на месте: лист меняется
 * целиком, когда новая таблица уже посчитана.
 *
 * ⚠️ Вход этого файла подключён через moduleGraph(): версия стоит в ПУТИ
 * (/jsv/<ver>/…), поэтому относительные import соседних модулей — и
 * динамический import('../lang/<code>.js') в загрузчике — получают ту же
 * версию. Строка запроса ?v= относительным импортам не передаётся.
 *
 * Состояние — в адресе: ?lang=es&verb=hablar (&cmp=estar, &mode=trainer),
 * history.replaceState без перезагрузки.
 */
import { createLoader } from './engine/loader.js';
import { CFG, LOCALE, $, t, tf, th, vb, raw, esc, num, langName, stage, funnel, diag, critical, caught, resultShown, loadSetting, saveSetting, toast, insertAtCaret, seg, unstress } from './ui.js';
import { moodsHtml, nonFiniteHtml, passportChips, schoolHtml, schoolReferenceHtml, sheetText, tensesOf, lineOf, speechOf, pronounOf, names, tenseNames, moodNames } from './render.js';
import { verbHtml, dirOf } from './i18n.js';
import { tidy } from './trainer.js';
import { baseOf, derivedOf, similarOnce } from './relatives.js';
import { suggestionHits, pickHit, splitOthers } from './hits.js';
import { queryVariants } from './query.js';
import { createTrainer } from './mode-trainer.js';
import { copySheet, savePdf, printSheet, exportAnki, shareLink } from './export.js';

const CODES = CFG.codes || ['en', 'es', 'fr', 'de', 'pt', 'it', 'ru', 'uk'];
const NAMES = CFG.names || {};
const DEFAULT_BY_LOCALE = { ru: 'ru', uk: 'uk', en: 'en', de: 'de', fr: 'fr', es: 'es', pt: 'pt', ko: 'en', ar: 'en', id: 'en' };
/**
 * Голос озвучки: регионы по порядку предпочтения. Испанский сайта — латиноамериканский,
 * поэтому es звучит голосом es-MX / es-US / es-419 (потом любым неиспанским es), а голос
 * Испании — только при включённом vosotros; pt — бразильский, Португалия — по выбору
 * варианта; en — US, UK — по выбору варианта.
 */
function voiceRegions(code, o) {
    switch (code) {
        case 'es': return o.vosotros ? ['es-ES'] : o.voseo ? ['es-AR', 'es-UY', 'es-419', 'es-*'] : ['es-MX', 'es-US', 'es-419', 'es-*'];
        case 'pt': return o.variety === 'pt' ? ['pt-PT'] : ['pt-BR'];
        case 'en': return o.variety === 'uk' ? ['en-GB'] : ['en-US'];
        case 'fr': return ['fr-FR'];
        case 'de': return ['de-DE'];
        case 'it': return ['it-IT'];
        case 'ru': return ['ru-RU'];
        case 'uk': return ['uk-UA'];
        default: return [code];
    }
}
/**
 * Умолчания опций, которые зависят от СТРАНЫ страницы, а не от языка глагола:
 * латиноамериканская школа таблицу с vosotros не учит (испанская страница сайта —
 * латиноамериканская), бразильская школа учит полную таблицу с vós.
 * Выбор человека (сохранённый) всегда сильнее.
 */
/**
 * Страна браузера для языка: первый тег navigator.languages с этим языком (как у калькуляторов серии,
 * решение 06.10: без сети, выбор человека сильнее). '' — тег без страны или языка нет.
 */
function browserRegion(lang) {
    try {
        const list = navigator.languages && navigator.languages.length ? navigator.languages : [navigator.language];
        for (const tag of list) {
            const parts = String(tag || '').split('-');
            if (parts[0].toLowerCase() !== lang) continue;
            return parts[1] && /^[A-Za-z]{2}$/.test(parts[1]) ? parts[1].toUpperCase() : '';
        }
    } catch (_) { /* браузер без navigator.languages */ }
    return '';
}
/*
 * Испанская страница по стране браузера (QA 14.10): Испания — таблица с vosotros (школа Испании её учит),
 * Аргентина, Уругвай, Парагвай — с vos (voseo — школьная норма там); остальные — латиноамериканская таблица.
 */
const ES_REGION = LOCALE === 'es' ? browserRegion('es') : '';
const ES_PAGE_DEFAULTS = ES_REGION === 'ES' ? { vosotros: true } : ['AR', 'UY', 'PY'].includes(ES_REGION) ? { vosotros: false, voseo: true } : { vosotros: false };
const LOCALE_OPTION_DEFAULTS = { es: { es: ES_PAGE_DEFAULTS }, pt: { pt: { vos: true } } };
// Пары для сравнения: то, что путают на уроке.
const CONFUSED_PAIRS = {
    es: [['ser', 'estar'], ['saber', 'conocer'], ['pedir', 'preguntar']],
    pt: [['ser', 'estar'], ['ter', 'haver'], ['saber', 'conhecer']],
    it: [['essere', 'avere'], ['sapere', 'conoscere'], ['andare', 'venire']],
    fr: [['être', 'avoir'], ['savoir', 'connaître'], ['aller', 'venir']],
    de: [['sein', 'haben'], ['werden', 'sein'], ['liegen', 'legen']],
    en: [['do', 'make'], ['say', 'tell'], ['lie', 'lay']],
    ru: [['делать', 'сделать'], ['идти', 'ходить'], ['лечь', 'лежать']],
    uk: [['робити', 'зробити'], ['писати', 'написати'], ['сидіти', 'сісти']],
};

const loader = createLoader({ manifests: CFG.manifests || {}, baseUrl: CFG.dataBase || '/data/verb-conjugation/' });

/** Всё, что знает страница. */
const S = {
    code: null,
    lang: null,
    mode: 'table',
    A: null, // {lemma, id, reflexive, res, hit, query}
    B: null,
    pronouns: true,
    say: false,
    busy: false,
    done: 0, // сколько поисков/открытий завершилось — опора для стенда
};

let langSeq = 0;
let openSeq = 0;
let langSeg;
let modeSeg;
let trainer;

/* ── настройки таблицы ───────────────────────────────────────────────────── */

function optionsFor(code) {
    const saved = loadSetting('opts_' + code, {}) || {};
    const meta = S.lang && S.lang.code === code ? S.lang.meta : null;
    const o = {};
    const local = (LOCALE_OPTION_DEFAULTS[LOCALE] || {})[code] || {};
    for (const op of (meta ? meta.options : []) || []) {
        const def = Object.prototype.hasOwnProperty.call(local, op.id) ? local[op.id] : op.default;
        o[op.id] = Object.prototype.hasOwnProperty.call(saved, op.id) && op.values.some((v) => v === saved[op.id]) ? saved[op.id] : def;
    }
    return o;
}
function setOption(id, value) {
    const cur = loadSetting('opts_' + S.code, {}) || {};
    cur[id] = value;
    saveSetting('opts_' + S.code, cur);
}

/* ── прогресс загрузки языка ─────────────────────────────────────────────── */

function showProgress(code, fraction) {
    const pct = Math.max(0, Math.min(100, Math.floor(fraction * 100)));
    const box = $('vcProg');
    box.hidden = false;
    $('vcStatTxt').hidden = true;
    $('vcProgBar').style.transform = 'scaleX(' + (pct / 100).toFixed(3) + ')';
    box.setAttribute('aria-valuenow', String(pct));
    $('vcProgTxt').textContent = tf('loading_lang', { lang: langName(code), pct: num(pct) });
}
function hideProgress() {
    $('vcProg').hidden = true;
    $('vcStatTxt').hidden = false;
}
function setStatus(html) {
    $('vcStatTxt').innerHTML = html;
}
function idleStatus() {
    if (!S.lang) { setStatus(''); return; }
    const n = S.lang.manifest && S.lang.manifest.counts ? S.lang.manifest.counts.verbs : 0;
    setStatus(th('lang_ready', { lang: langName(S.code), n: num(n) }));
}
function setBusy(on) {
    S.busy = on;
    const st = $('vcStage');
    st.classList.toggle('vc-busy', on);
    st.setAttribute('aria-busy', on ? 'true' : 'false');
}

/* ── выбор языка ─────────────────────────────────────────────────────────── */

async function selectLang(code, o = {}) {
    if (!CODES.includes(code)) code = CODES[0];
    if (code === S.code && S.lang && !o.force) {
        if (o.verb) await submit(o.verb, 'A', { quiet: true });
        return;
    }
    const my = ++langSeq;
    langSeg.set(code);
    const cached = loader.isLoaded(code);
    if (!cached) showProgress(code, 0);
    setBusy(true);
    let lang;
    try {
        lang = await loader.load(code, {
            onProgress: (p) => { if (my === langSeq && !cached) showProgress(code, p.fraction); },
        });
    } catch (e) {
        if (my !== langSeq) return;
        hideProgress();
        setBusy(false);
        langSeg.set(S.code);
        const why = e && e.code === 'network' ? t('err_network') : e && e.code === 'http' ? t('err_http') : t('err_data');
        setStatus(`<span class="vc-stat-err">${th('err_lang', { lang: langName(code), why })}</span> `
            + `<button type="button" class="tc-btn sm ghost" id="vcRetry">${esc(t('retry'))}</button>`);
        const rb = $('vcRetry');
        if (rb) rb.addEventListener('click', () => selectLang(code, o));
        // словарь языка не доехал — критический сигнал (доходит и до согласия) и строка «Повторить»
        critical('vc_lang_failed', { code, err: (e && e.code) || 'unknown', msg: String((e && e.message) || '').slice(0, 120) });
        return;
    }
    if (my !== langSeq) return;
    const hadVerb = !!S.A;
    S.code = code;
    S.lang = lang;
    if (o.byUser) saveSetting('lang', code);
    setupLangUi();
    stage('lang_loaded', { code });
    if (o.byUser) funnel('lang', { code, cached: cached ? 1 : 0 });
    const target = o.verb || (hadVerb ? (lang.top(1)[0] || {}).lemma : null);
    S.A = null;
    S.B = null;
    // глагол, открытый самой страницей при смене языка, — не запрос человека (by: 'auto' в tool_complete)
    if (target) await submit(target, 'A', { quiet: !o.verb, keepBusy: true, auto: !o.verb });
    else renderSheet();
    if (o.cmp && S.A) await submit(o.cmp, 'B', { quiet: true, keepBusy: true });
    if (my !== langSeq) return;
    hideProgress();
    idleStatus();
    setBusy(false);
    checkVoice();
    trainer.onLang();
    syncUrl();
}

/** Всё, что зависит от языка глагола: клавиши, частые глаголы, опции, подсказки. */
function setupLangUi() {
    const meta = S.lang.meta;
    const code = S.code;
    for (const id of ['vcQ', 'vcQ2']) {
        const q = $(id);
        q.value = '';
        q.setAttribute('lang', code);
        q.placeholder = tf('search_placeholder', { examples: (CFG.examples && CFG.examples[code]) || '' });
        q.removeAttribute('aria-invalid');
    }
    closeMenu('A');
    closeMenu('B');
    showMsg('');
    // экранные клавиши диакритики
    // одна остановка Tab на панель (role=toolbar), внутри — стрелки: у fr 14 клавиш, и Tab шёл через каждую
    const keys = (meta.diacritics || []).map((d, i) => `<button type="button" class="vc-key" data-vc-key="${esc(d)}" lang="${code}" tabindex="${i ? -1 : 0}" aria-label="${esc(tf('key_insert', { ch: d }))}">${esc(d)}</button>`).join('');
    for (const id of ['vcKeys', 'vcTrKeys']) {
        const box = $(id);
        box.innerHTML = keys;
        box.hidden = !keys;
    }
    renderRecent();
    // частые глаголы
    const top = S.lang.top(12);
    $('vcTop').innerHTML = top.map((x) => `<button type="button" class="tc-chip" data-vc-open="${esc(x.lemma)}" data-vc-id="${x.id}" lang="${code}" dir="${dirOf(code)}">${esc(x.lemma)}</button>`).join('');
    // пары для сравнения
    $('vcPairs').innerHTML = (CONFUSED_PAIRS[code] || []).map(([a, b]) => `<button type="button" class="tc-chip" data-vc-pair="${esc(a + '|' + b)}" lang="${code}" dir="${dirOf(code)}">${esc(a)} ↔ ${esc(b)}</button>`).join('');
    renderOptionsBar();
}

/* ── опции таблицы ───────────────────────────────────────────────────────── */

/** Вставки в подписи опций модулей (opt_*): буква языка глагола — не буквой в переводе. */
const OPTION_VARS = { yo: vb('ё', 'ru') };

function renderOptionsBar() {
    const meta = S.lang.meta;
    const cur = optionsFor(S.code);
    let h = `<label class="vc-chk"><input type="checkbox" id="vcOptPron"${S.pronouns ? ' checked' : ''}> ${esc(t('opt_pronouns'))}</label>`;
    for (const op of meta.options || []) {
        const isBool = op.values.length === 2 && op.values.every((v) => typeof v === 'boolean');
        if (isBool) {
            h += `<label class="vc-chk"><input type="checkbox" data-vc-opt="${esc(op.id)}"${cur[op.id] ? ' checked' : ''}> ${th(op.key, OPTION_VARS)}</label>`;
        } else {
            h += `<span class="vc-optseg"><span class="vc-optseg-l">${th(op.key, OPTION_VARS)}</span><span class="tc-seg" role="radiogroup" aria-label="${esc(tf(op.key, OPTION_VARS))}">`
                + op.values.map((v) => `<button type="button" class="tc-seg-b${cur[op.id] === v ? ' on' : ''}" role="radio" aria-checked="${cur[op.id] === v ? 'true' : 'false'}" data-vc-optv="${esc(op.id + '|' + v)}">${esc(t(op.key + '_' + v))}</button>`).join('')
                + '</span></span>';
        }
    }
    $('vcOpts').innerHTML = h;
}

function onOptionsChange(e) {
    const el = e.target;
    if (el.id === 'vcOptPron') {
        S.pronouns = el.checked;
        saveSetting('pronouns', S.pronouns);
        renderSheet();
        funnel('setting', { id: 'pronouns', v: String(S.pronouns) });
        return;
    }
    const id = el.getAttribute('data-vc-opt');
    if (!id) return;
    setOption(id, el.checked);
    funnel('setting', { id, v: String(el.checked), code: S.code });
    refresh();
}
function onOptionsClick(e) {
    const b = e.target.closest('[data-vc-optv]');
    if (!b) return;
    const [id, raw] = b.getAttribute('data-vc-optv').split('|');
    const op = (S.lang.meta.options || []).find((x) => x.id === id);
    if (!op) return;
    const v = op.values.find((x) => String(x) === raw);
    setOption(id, v);
    b.parentElement.querySelectorAll('.tc-seg-b').forEach((x) => {
        const on = x === b;
        x.classList.toggle('on', on);
        x.setAttribute('aria-checked', on ? 'true' : 'false');
    });
    funnel('setting', { id, v: String(v), code: S.code });
    refresh();
}

/** Пересчитать показанные глаголы с новыми опциями. */
async function refresh() {
    const my = ++openSeq;
    const opts = optionsFor(S.code);
    const redo = async (x) => (x ? Object.assign({}, x, { res: await S.lang.conjugate(x.lemma, Object.assign({}, opts, { id: x.id, reflexive: x.reflexive })) || x.res }) : null);
    const A = await redo(S.A);
    const B = await redo(S.B);
    if (my !== openSeq) return;
    S.A = A;
    S.B = B;
    renderSheet();
    trainer.onOptions();
}

/* ── поиск ───────────────────────────────────────────────────────────────── */

const menus = {
    A: { input: () => $('vcQ'), list: () => $('vcSug'), hits: [], active: -1, seq: 0, timer: 0 },
    B: { input: () => $('vcQ2'), list: () => $('vcSug2'), hits: [], active: -1, seq: 0, timer: 0 },
};

function closeMenu(which) {
    const m = menus[which];
    m.hits = [];
    m.active = -1;
    const l = m.list();
    l.hidden = true;
    l.innerHTML = '';
    m.input().setAttribute('aria-expanded', 'false');
    m.input().removeAttribute('aria-activedescendant');
}

function sugText(h) {
    const d = S.lang.describe ? S.lang.describe(h.id) : null;
    const shown = d ? d.shown : h.lemma;
    let sub = '';
    if (h.match === 'form' && h.matchedForm) sub = th('sug_form', { form: vb(h.matchedForm, S.code) });
    else if (h.reflexive) sub = esc(t('sug_reflexive'));
    const gloss = d && d.gloss && LOCALE === 'en' ? d.gloss.replace(/\s*\(.*$/, '').slice(0, 42) : '';
    return { shown, sub, gloss };
}

async function suggest(which) {
    const m = menus[which];
    const q = m.input().value.trim();
    const my = ++m.seq;
    if (!q || !S.lang) { closeMenu(which); return; }
    let hits = [];
    // «I went», «hablar.» — подсказки по тому же варианту запроса, что найдёт Enter (query.js)
    for (const v of queryVariants(q, S.code)) {
        try { hits = await S.lang.lookup(v, { limit: 8 }); } catch (e) { hits = []; }
        if (my !== m.seq || !S.lang) return;
        if (hits.some((h) => h.match !== 'prefix')) break;
    }
    if (my !== m.seq || !S.lang) return;
    // «went» — форма go; диалектное gan с той же формой в выборе только мешает (hits.js)
    hits = suggestionHits(hits);
    m.hits = hits;
    m.active = -1;
    const l = m.list();
    if (!hits.length) {
        l.innerHTML = `<li class="tc-empty" role="presentation">${esc(t('sug_none'))}</li>`;
        l.hidden = false;
        m.input().setAttribute('aria-expanded', 'true');
        return;
    }
    l.innerHTML = hits.map((h, i) => {
        const s = sugText(h);
        return `<li role="option" class="tc-opt" id="vcOpt${which}${i}" data-i="${i}" aria-selected="false">`
            + `<b lang="${S.code}" dir="${dirOf(S.code)}">${esc(s.shown)}</b>`
            + (s.sub ? `<span class="vc-opt-sub">${s.sub}</span>` : '')
            + (s.gloss ? `<span class="tc-opt-s" lang="en" dir="ltr">${glossHtml(s.gloss, S.code)}</span>` : '') + '</li>';
    }).join('');
    l.hidden = false;
    m.input().setAttribute('aria-expanded', 'true');
}

function moveActive(which, d) {
    const m = menus[which];
    if (!m.hits.length) return;
    m.active = (m.active + d + m.hits.length) % m.hits.length;
    m.list().querySelectorAll('.tc-opt').forEach((el, i) => {
        const on = i === m.active;
        el.classList.toggle('cur', on);
        el.setAttribute('aria-selected', on ? 'true' : 'false');
        if (on) el.scrollIntoView({ block: 'nearest' });
    });
    m.input().setAttribute('aria-activedescendant', 'vcOpt' + which + m.active);
}

function bindSearch(which) {
    const m = menus[which];
    const input = m.input();
    input.addEventListener('input', () => {
        input.removeAttribute('aria-invalid');
        clearTimeout(m.timer);
        m.timer = setTimeout(() => suggest(which), 90);
        if (which === 'A') $('vcQClear').hidden = !input.value;
    });
    input.addEventListener('keydown', (e) => {
        if (e.key === 'ArrowDown') { e.preventDefault(); if (m.list().hidden) suggest(which); else moveActive(which, 1); }
        else if (e.key === 'ArrowUp') { e.preventDefault(); moveActive(which, -1); }
        else if (e.key === 'Escape') { closeMenu(which); }
        else if (e.key === 'Enter') {
            e.preventDefault();
            clearTimeout(m.timer);
            const h = m.active >= 0 ? m.hits[m.active] : null;
            closeMenu(which);
            if (h) openHit(h, which, input.value.trim());
            else submit(input.value, which);
        }
    });
    input.addEventListener('blur', () => setTimeout(() => closeMenu(which), 160));
    m.list().addEventListener('mousedown', (e) => e.preventDefault());
    m.list().addEventListener('click', (e) => {
        const li = e.target.closest('[data-i]');
        if (!li) return;
        const h = m.hits[Number(li.getAttribute('data-i'))];
        const q = input.value.trim();
        closeMenu(which);
        if (h) openHit(h, which, q);
    });
}

function showMsg(html, kind) {
    const box = $('vcMsg');
    box.className = 'tc-msg' + (kind ? ' ' + kind : '');
    box.innerHTML = html || '';
    box.hidden = !html;
}

/** Поиск по нажатию Enter / из адреса / по чипу. */
async function submit(query, which = 'A', o = {}) {
    const q = String(query || '').trim();
    const input = menus[which].input();
    if (!S.lang) return;
    if (!q) {
        if (which === 'A') { S.A = null; renderSheet(); syncUrl(); }
        return;
    }
    // Варианты запроса (query.js): как набрано без кавычек и точки → двойники букв → без местоимения
    // («I went», «yo hablo», «я пишу») → последнее/первое слово («bin gegangen»). Первый найденный — ответ.
    const variants = queryVariants(q, S.code);
    const shown = variants[0] || q;
    let hits = [];
    let firstHits = null;
    let failed = null;
    let pick = null;
    let rest = [];
    let used = shown;
    for (const v of variants) {
        try { hits = await S.lang.lookup(v, { limit: 12 }); } catch (e) { hits = []; failed = e; break; }
        if (!firstHits) firstHits = hits;
        const r = pickHit(hits);
        if (r.pick) { pick = r.pick; rest = r.others; used = v; break; }
    }
    if (pick) {
        await openHit(pick, which, used, { others: rest, quiet: o.quiet, keepBusy: o.keepBusy, typed: q, auto: o.auto });
        S.done++;
        return;
    }
    hits = firstHits || [];
    if (failed) {
        // Часть словаря не догрузилась (сеть). Это не «не глагол» и не повод строить таблицу по
        // правилам: настоящий глагол из недогруженной части получил бы выдуманные правильные формы.
        lookupFailed(failed, () => submit(q, which, o));
        S.done++;
        return;
    }
    // Нет в словаре — таблица по правилам языка, если слово похоже на глагол.
    const my = ++openSeq;
    let res = null;
    // правила — для одного слова: «I flarbed» строит таблицу «flarbed», а не «I flarbed»
    const ruleQ = /\s/.test(shown) ? (variants.find((v) => !/\s/.test(v)) || shown) : shown;
    try { res = await S.lang.conjugate(ruleQ, optionsFor(S.code)); } catch (e) { res = null; }
    if (my !== openSeq) return;
    if (res) {
        setVerb(which, { lemma: res.lemma, id: null, reflexive: false, res, hit: null, query: ruleQ }, { via: 'guessed', by: o.auto ? 'auto' : 'user' });
        if (which === 'A') showMsg(th('msg_guessed', { q: vb(ruleQ, S.code) }), 'warn');
        diag('guessed', q, { code: S.code });
        S.done++;
        return;
    }
    input.setAttribute('aria-invalid', 'true');
    const other = scriptHint(q);
    const prefix = hits.filter((h) => h.match === 'prefix').slice(0, 5);
    let html = th('msg_not_found', { q: vb(shown, S.code), lang: langName(S.code) });
    if (prefix.length) html += ' ' + th('msg_maybe', { list: verbList(prefix.map((h) => ({ lemma: h.lemma, id: h.id, shown: h.lemma }))) });
    if (other.length) {
        // кнопки языков подписаны самоназваниями — как в переключателе над прибором
        const langs = other.map((c) => `<button type="button" class="vc-lnk" data-vc-switch="${c}" data-vc-q="${esc(q)}" lang="${c}">${esc(NAMES[c] || c)}</button>`).join(' · ');
        html += ' ' + th('msg_other_script', { list: raw(`<bdi dir="ltr">${langs}</bdi>`) });
    }
    showMsg(html, 'bad');
    diag(other.length ? 'other_script' : 'not_found', q, { code: S.code, maybe: prefix.length });
    S.done++;
}

/** Словарь не ответил на поиск: сказать причину и дать повторить — тем же текстом, что при загрузке языка. */
function lookupFailed(e, retry) {
    const why = e && e.code === 'network' ? t('err_network') : e && e.code === 'http' ? t('err_http') : t('err_data');
    showMsg(`${th('err_lang', { lang: langName(S.code), why })} <button type="button" class="tc-btn sm ghost" id="vcRetryQ">${esc(t('retry'))}</button>`, 'bad');
    const rb = $('vcRetryQ');
    if (rb) rb.addEventListener('click', retry, { once: true });
    critical('vc_lookup_failed', { code: S.code, err: (e && e.code) || 'unknown' });
}

/** Кириллица в латинском языке и наоборот — предложить язык по письму. */
function scriptHint(q) {
    const cyr = /[а-яёіїєґ]/i.test(q);
    const lat = /[a-zà-ÿßœæ]/i.test(q);
    const cyrLang = S.code === 'ru' || S.code === 'uk';
    // Ukrainian or Russian first: the site's one detector (timbrica-core), not «is there an і».
    if (cyr && !cyrLang) return window.__cyrillicLang && window.__cyrillicLang(q) === 'uk' ? ['uk', 'ru'] : ['ru', 'uk'];
    if (lat && cyrLang) return ['en', 'es', 'fr', 'de', 'pt', 'it'];
    return [];
}

async function openHit(h, which, query, o = {}) {
    const my = ++openSeq;
    let res = null;
    try {
        res = await S.lang.conjugate(h.lemma, Object.assign({}, optionsFor(S.code), { id: h.id, reflexive: !!h.reflexive }));
    } catch (e) {
        caught(e, 'vc_conjugate', tf('err_lang', { lang: langName(S.code), why: t('err_data') }));
    }
    if (my !== openSeq) return;
    if (!res) { showMsg(th('msg_not_found', { q: vb(query || h.lemma, S.code), lang: langName(S.code) }), 'bad'); S.done++; return; }
    const hit = h.match === 'form' ? h.matchedForm : null;
    setVerb(which, { lemma: h.lemma, id: h.id, reflexive: !!h.reflexive, res, hit, query }, { via: h.match, by: o.auto ? 'auto' : 'user', reduced: o.typed && tidyQ(o.typed) !== tidyQ(query) ? 1 : 0 });
    if (which === 'A') {
        const others = o.others || [];
        // Глаголы без частотного свидетельства рядом с частым — отдельной фразой «менее частые»:
        // «went» — форма go, а не «go и gan» (hits.js).
        const { main, homographs, sharers, rare } = splitOthers(h, others);
        const links = (arr) => verbList(arr.map((x) => ({ lemma: x.lemma, id: x.id, hit: x.match === 'form' ? (x.matchedForm || '') : '', shown: shownOf(x) })));
        const parts = [];
        if (hit) {
            const where = whereIs(res, hit);
            parts.push(where
                ? th('msg_form_of', { form: vb(hit, S.code), lemma: vb(res.lemma, S.code), where: raw(where) })
                : th('msg_form_of_short', { form: vb(hit, S.code), lemma: vb(res.lemma, S.code) }));
            if (main.length) parts.push(th('msg_form_also', { list: links(main) }));
            if (rare.length) parts.push(th('msg_form_rare', { list: links(rare) }));
            showMsg(parts.join(' '), main.length ? 'warn' : '');
        } else if (others.length && !o.quiet) {
            // набран инфинитив: «другой глагол с тем же написанием» — только омографы (lie / lie);
            // глагол, у которого это слово — форма (saw → see), назван как форма
            if (homographs.length) parts.push(th('msg_homographs', { list: links(homographs) }));
            if (sharers.length) parts.push(th('msg_form_also', { list: links(sharers) }));
            if (rare.length) parts.push(th('msg_form_rare', { list: links(rare) }));
            showMsg(parts.join(' '), '');
        } else showMsg('');
        const input = $('vcQ');
        if (document.activeElement !== input) input.value = h.lemma;
        $('vcQClear').hidden = !input.value;
    } else {
        $('vcQ2').value = h.lemma;
    }
    S.done++;
}

/**
 * Список глаголов-ссылок как значение подстановки: кнопки на языке глагола через запятую,
 * изолированные одним <bdi> — на арабской странице запятые и порядок не выворачиваются.
 */
function verbList(items) {
    const code = S.code;
    const btns = items.map((x) => `<button type="button" class="vc-lnk" data-vc-open="${esc(x.lemma)}"`
        + (x.id != null ? ` data-vc-id="${x.id}"` : '') + (x.hit ? ` data-vc-hit="${esc(x.hit)}"` : '')
        + ` lang="${code}">${esc(x.shown || x.lemma)}</button>`);
    // запятая держится своего глагола: иначе перенос строки ставил её первой в строке («, surprendre»)
    const list = btns.map((b, i) => (i < btns.length - 1 ? `<span class="vc-li">${b},</span>` : b)).join(' ');
    return raw(`<bdi lang="${code}" dir="${dirOf(code)}">${list}</bdi>`);
}

/** Подпись глагола-ссылки: у омографов ru/uk — с ударением (пи́сать / писа́ть). */
function shownOf(h) {
    const d = S.lang && S.lang.describe ? S.lang.describe(h.id) : null;
    return d ? d.shown : h.lemma;
}

/**
 * Где в таблице стоит найденная форма — HTML для msg_form_of. Названия времён — те же,
 * что на карточках (собственные термины языка глагола), местоимения — вставки языка
 * глагола; связки — шаблоны where_in_mood / where_who / where_sep.
 */
function whereIs(res, form) {
    const f = tidy(form);
    const out = [];
    const meta = S.lang.meta;
    const code = S.code;
    // наклонение называем, только если то же время есть в другом наклонении (Presente: Indicativo/Subjuntivo)
    const keyCount = {};
    meta.moods.forEach((m) => m.tenses.forEach((x) => { keyCount[x.native || x.key] = (keyCount[x.native || x.key] || 0) + 1; }));
    const nameHtml = (nm) => (nm.mainLang ? verbHtml(nm.main, nm.mainLang) : esc(nm.main));
    const said = new Set(); // названия, уже стоящие в ответе
    for (const { mood, tenses } of tensesOf(meta, res)) {
        for (const tn of tenses) {
            const rows = res.table[mood.id][tn.id].filter((r) => r.form != null);
            const hits = rows.filter((r) => tidy(r.form) === f || (r.alt || []).some((a) => tidy(a) === f));
            if (!hits.length) continue;
            const tnm = tenseNames(code, mood, tn);
            said.add(tidy(tnm.main));
            let label = nameHtml(tnm);
            if (keyCount[tn.native || tn.key] > 1) {
                label = th('where_in_mood', { tense: raw(label), mood: raw(nameHtml(moodNames(code, mood))) });
            }
            const who = hits.length === rows.length ? [] : hits.map(pronounOf).filter(Boolean);
            out.push(who.length ? th('where_who', { tense: raw(label), who: vb(who.join(', '), code) }) : label);
        }
    }
    for (const nf of meta.nonFinite || []) {
        const v = res.nonFinite && res.nonFinite[nf.id];
        if (!v || tidy(v) !== f) continue;
        // английская 2-я форма «Past simple» — то же, что уже названное время «Past Simple»: один раз
        const nm = names(nf.key, nf.native, code);
        if (said.has(tidy(nm.main))) continue;
        said.add(tidy(nm.main));
        out.push(nameHtml(nm));
    }
    return out.slice(0, 3).join(esc(t('where_sep'))) + (out.length > 3 ? '…' : '');
}

const tidyQ = (x) => String(x || '').trim().toLowerCase();

/* ── недавние глаголы (настройки тула, отдельно по языку глагола) ── */
const RECENT_MAX = 8;
function recentList(code) {
    const v = loadSetting('recent_' + code, []);
    return Array.isArray(v) ? v.filter((x) => x && typeof x.lemma === 'string').slice(0, RECENT_MAX) : [];
}
function pushRecent(v) {
    if (!v || v.id == null || !S.code) return; // глагол из словаря: таблица «по правилам» в историю не идёт
    // возвратный (levantarse, se lever) — по записи: поиск находит его сам; остальные — по номеру в словаре
    const item = v.reflexive ? { lemma: unstress(v.res.lemma), id: null } : { lemma: v.lemma, id: v.id };
    const list = recentList(S.code).filter((x) => x.lemma !== item.lemma);
    list.unshift(item);
    saveSetting('recent_' + S.code, list.slice(0, RECENT_MAX));
    renderRecent();
}
function renderRecent() {
    const box = $('vcRecentBox');
    if (!box || !S.code) return;
    // текущий глагол первым не повторяется: недавние — куда вернуться
    const cur = S.A ? [S.A.lemma, unstress(S.A.res.lemma)] : [];
    const list = recentList(S.code).filter((x) => !cur.includes(x.lemma));
    box.hidden = !list.length;
    $('vcRecent').innerHTML = list.map((x) => `<button type="button" class="tc-chip" data-vc-open="${esc(x.lemma)}"${x.id != null ? ` data-vc-id="${x.id}"` : ''} lang="${S.code}" dir="${dirOf(S.code)}">${esc(x.lemma)}</button>`).join('');
}

/**
 * Глагол на листе. meta: {via: lemma|form|reflexive|guessed, by: user|auto, reduced: 1 — найден по
 * варианту запроса (без местоимения, кавычек)} — в tool_complete, без самого запроса.
 */
function setVerb(which, v, meta) {
    if (which === 'B') S.B = v;
    else S.A = v;
    renderSheet(true);
    syncUrl();
    trainer.onVerb();
    stage('verb_shown', { code: S.code });
    const m = meta || {};
    if (which === 'A' && m.by !== 'auto') pushRecent(v); else renderRecent();
    const sig = [S.code, S.mode, S.A ? S.A.res.lemma : '', S.mode === 'compare' && S.B ? S.B.res.lemma : ''].join('|');
    resultShown(sig, { code: S.code, via: m.via || 'lemma', by: m.by || 'user', mode: S.mode === 'compare' ? 'compare' : 'table', which, reduced: m.reduced || 0 });
}

/* ── лист ────────────────────────────────────────────────────────────────── */

function headHtml(v, compact) {
    const res = v.res;
    const code = S.code;
    const info = res.info || {};
    // метка — фраза на языке страницы: направление страницы (без dir="auto": «A-B-C: …» на арабской
    // странице вставала слева направо); вставки языка глагола изолирует <bdi lang dir> (vb)
    const chips = passportChips(code, info, res).map((c) => `<li class="vc-pp${c.kind ? ' ' + c.kind : ''}">${c.html}</li>`).join('');
    const nf = res.nonFinite || {};
    const pp = code === 'en' && nf.past && nf.participle
        ? `<p class="vc-parts3">${verbHtml([unstress(res.lemma), nf.past, nf.participle].join(' — '), 'en')}</p>` : '';
    const sayInf = S.say ? `<button type="button" class="vc-say-inf no-print" data-say="${esc(unstress(code === 'en' ? (nf.infinitive || res.lemma) : res.lemma))}" aria-label="${esc(t('say_form'))}" title="${esc(t('say_form'))}">🔊</button>` : '';
    const guessed = res.guessed ? `<p class="tc-msg warn vc-guess">${esc(t('pp_guessed'))}</p>` : '';
    return `<div class="vc-head${compact ? ' vc-head-c' : ''}">`
        + `<div class="vc-title-row"><h3 class="vc-verb" lang="${code}" dir="${dirOf(code)}">${esc(res.lemma)}</h3>${sayInf}<span class="vc-langtag" lang="${code}">${esc(NAMES[code] || code)}</span></div>`
        + pp
        + `<p class="vc-gloss" data-vc-gloss hidden></p>`
        + (chips ? `<ul class="vc-pps">${chips}</ul>` : '')
        + (compact ? '' : `<p class="vc-model" data-vc-model></p>`)
        + guessed + '</div>';
}

function renderSheet(fresh) {
    const code = S.code;
    const meta = S.lang ? S.lang.meta : null;
    const empty = !S.A || !meta;
    $('vcEmpty').hidden = !empty;
    $('vcFull').hidden = empty;
    const cmp = S.mode === 'compare';
    $('vcCmpBox').hidden = !cmp;
    document.querySelectorAll('[data-vc-need-verb]').forEach((b) => { b.disabled = empty; });
    if (empty) {
        $('vcEmptyRef').innerHTML = meta ? schoolReferenceHtml(code, meta) : '';
        return;
    }
    const B = cmp && S.B ? S.B : null;
    const o = {
        code, pronouns: S.pronouns, say: S.say, hit: B ? null : S.A.hit,
        lemmaA: S.A.res.lemma, lemmaB: B ? B.res.lemma : '',
    };
    $('vcHead').innerHTML = B
        ? `<div class="vc-heads">${headHtml(S.A, true)}${headHtml(B, true)}</div>`
        : headHtml(S.A, false);
    $('vcHead').classList.toggle('two', !!B);
    $('vcSchool').innerHTML = B ? '' : schoolHtml(code, meta, S.A.res, o);
    $('vcBody').innerHTML = moodsHtml(meta, S.A.res, o, B ? B.res : null) + nonFiniteHtml(meta, S.A.res, o, B ? B.res : null);
    $('vcLegend').hidden = false;
    const legEnd = document.querySelector('[data-vc-leg-end]');
    if (legEnd) legEnd.hidden = !(code === 'ru' || code === 'uk');
    fillModel();
    fillGloss();
    if (fresh) {
        $('vcFull').classList.remove('tc-flash');
        void $('vcFull').offsetWidth;
        $('vcFull').classList.add('tc-flash');
        // глагол — вставка с lang: скринридер читает «идти» русским голосом, а не английским
        $('vcLive').innerHTML = th('live_shown', { lemma: vb(unstress(S.A.res.lemma) + (B ? ' / ' + unstress(B.res.lemma) : ''), code) });
    }
}

/** «Спрягается как pasar · так же: …» — догружается асинхронно. */
async function fillModel() {
    const box = document.querySelector('[data-vc-model]');
    if (!box || !S.A) return;
    const v = S.A;
    const info = v.res.info || {};
    if (v.res.guessed || !info.modelLemma) { box.hidden = true; return; }
    const size = info.paradigmSize || 0;
    let sim = [];
    try { sim = await S.lang.similar(v.lemma, 24); } catch (e) { sim = []; }
    if (S.A !== v) return;
    const one = (l) => raw(`<button type="button" class="vc-lnk" data-vc-open="${esc(l)}" lang="${S.code}" dir="${dirOf(S.code)}">${esc(l)}</button>`);
    const list = (arr) => verbList(arr.map((l) => ({ lemma: l, shown: l })));
    // родня по приставке: tener → mantener, obtener; написать ← писать
    let base = null;
    let derived = [];
    try {
        base = baseOf(S.lang, v);
        derived = derivedOf(S.lang, v, 8);
    } catch (e) { base = null; derived = []; }
    // Каждый глагол назван один раз. Приставочный глагол той же парадигмы стоял в обоих списках
    // (get: reget, unget, overget — дважды): он остаётся во втором — там сказано, ЧЕМ он похож.
    // Образец («спрягается как…») и основа («как … с приставкой») в «так же» не повторяются.
    // Видовая пара ru/uk (писать — написать) — другое отношение со своей кнопкой сравнения:
    // она может повторить глагол из «так же с приставками», и это не дубль.
    const likeModel = size > 1 && !(tidy(info.modelLemma) === tidy(v.res.lemma) || tidy(info.modelLemma) === tidy(v.lemma));
    const pair = (S.code === 'ru' || S.code === 'uk') ? (info.aspectPair || []) : [];
    sim = similarOnce(sim, derived.concat(likeModel ? [info.modelLemma] : base ? [base.base] : []), tidy, 8);
    let h;
    if (likeModel) {
        h = th('pp_model', { model: one(info.modelLemma), n: num(size - 1) });
    } else if (base) {
        h = th('pp_like_base', { base: one(base.base), prefix: vb(base.prefix, S.code) });
    } else if (size > 1) {
        h = th('pp_model_self', { n: num(size - 1) });
    } else if (!derived.length) {
        h = esc(t('pp_unique'));
    } else h = '';
    if (sim.length && size > 1) h += ` <span class="vc-sim">${th('pp_similar', { list: list(sim) })}</span>`;
    if (derived.length) h += ` <span class="vc-sim">${th('pp_derived', { list: list(derived) })}</span>`;
    if (pair.length) h += ` <span class="vc-sim">${th('pp_pair', { list: list(pair) })} <button type="button" class="vc-lnk" data-vc-cmp="${esc(pair[0])}">${esc(t('pp_pair_compare'))}</button></span>`;
    if (S.code === 'en' && info.irregularVerb && CFG.links && CFG.links.irregular) {
        h += ` <a class="vc-lnk" href="${esc(CFG.links.irregular)}">${esc(t('link_irregular'))}</a>`;
    }
    box.innerHTML = h;
    box.hidden = false;
}

/** Перевод на язык страницы: общий LangGloss для английского слова. */
let glossReady = null;
function glossDict() {
    if (glossReady) return glossReady;
    if (!window.LangGloss || LOCALE === 'en') { glossReady = Promise.resolve(false); return glossReady; }
    glossReady = window.LangGloss.load(LOCALE).then((m) => !!m, () => false);
    return glossReady;
}
/**
 * Английское толкование Викисловаря: слова языка глагола внутри него («alternative form
 * of іти́») — вставки с lang, иначе скринридер читает их английским голосом. Различимы
 * только в кириллице; в латинице слово языка глагола от английского по письму не отличить.
 */
function glossHtml(gloss, code) {
    if (code !== 'ru' && code !== 'uk') return esc(gloss);
    const CYR = /[\p{Script=Cyrillic}][\p{Script=Cyrillic}\p{M}'’ʼ-]*/gu;
    let out = '';
    let last = 0;
    for (const m of String(gloss).matchAll(CYR)) {
        out += esc(gloss.slice(last, m.index)) + verbHtml(m[0], code);
        last = m.index + m[0].length;
    }
    return out + esc(gloss.slice(last));
}
function glossHead(gloss) {
    // «to be (essentially …)» → «be»; многословное толкование не переводим по слову
    const g = String(gloss || '').replace(/\(.*?\)/g, '').split(/[;,]/)[0].trim().replace(/^to\s+/i, '');
    return /^[a-z]+$/i.test(g) ? g.toLowerCase() : '';
}
async function fillGloss() {
    const boxes = document.querySelectorAll('[data-vc-gloss]');
    const verbs = S.mode === 'compare' && S.B ? [S.A, S.B] : [S.A];
    for (let i = 0; i < boxes.length && i < verbs.length; i++) {
        const v = verbs[i];
        const box = boxes[i];
        const gloss = v.res.info && v.res.info.gloss ? String(v.res.info.gloss) : '';
        const code = S.code;
        let local = '';
        if (LOCALE !== 'en' && LOCALE !== code) {
            const word = code === 'en' ? unstress(v.res.lemma).toLowerCase() : glossHead(gloss);
            if (word && await glossDict()) {
                try {
                    await window.LangGloss.ensure([word]);
                    const g = window.LangGloss.lookup(word, 'verb');
                    if (g && (g.pos === 'v' || code !== 'en')) local = g.text;
                } catch (e) { local = ''; }
            }
        }
        if (S.A !== verbs[0]) return;
        let h = '';
        if (local) h += `<span class="vc-gl-loc">${code === 'en' ? '' : '≈ '}${esc(local)}</span>`;
        if (gloss && code !== LOCALE && !(code === 'en' && local)) h += `<span class="vc-gl-en" lang="en" dir="ltr">${glossHtml(gloss, code)}</span>`;
        box.innerHTML = h;
        box.hidden = !h;
    }
}

/* ── режимы ──────────────────────────────────────────────────────────────── */

function setMode(m, byUser) {
    if (!['table', 'compare', 'trainer'].includes(m)) m = 'table';
    const apply = () => {
        S.mode = m;
        modeSeg.set(m);
        $('vcSheet').hidden = m === 'trainer';
        $('vcTrainer').hidden = m !== 'trainer';
        if (m === 'trainer') trainer.show();
        else renderSheet();
        if (m === 'compare' && !S.B && byUser) setTimeout(() => $('vcQ2').focus(), 30);
    };
    if (byUser && window.__viewTransition) window.__viewTransition(apply);
    else apply();
    if (byUser) { funnel('mode', { m }); syncUrl(); }
}

/* ── адрес ───────────────────────────────────────────────────────────────── */

let urlTimer = 0;
/** Записать состояние в адрес сейчас же; вернуть адрес для «Поделиться» (без nocache). */
function writeUrl() {
    clearTimeout(urlTimer);
    try {
        const u = new URL(location.href);
        const p = u.searchParams;
        p.set('lang', S.code || '');
        if (S.A) p.set('verb', unstress(S.A.res.lemma)); else p.delete('verb');
        if (S.mode === 'compare' && S.B) p.set('cmp', unstress(S.B.res.lemma)); else p.delete('cmp');
        if (S.mode !== 'table') p.set('mode', S.mode); else p.delete('mode');
        history.replaceState(history.state, '', u.pathname + '?' + p.toString() + u.hash);
        p.delete('nocache');
        return u.pathname === location.pathname ? u.origin + u.pathname + '?' + p.toString() + u.hash : location.href;
    } catch (_) { return location.href; /* песочница без history */ }
}
function syncUrl() {
    clearTimeout(urlTimer);
    urlTimer = setTimeout(writeUrl, 120);
}

/* ── озвучка ─────────────────────────────────────────────────────────────── */

/** Голос под язык глагола и выбранный вариант: {voice, lang}. */
function pickVoice() {
    const T = window.TTSHelper;
    const regions = voiceRegions(S.code, optionsFor(S.code));
    const fallbackLang = regions[0].replace('*', '');
    if (!T || !T.rankedVoices) return { voice: null, lang: fallbackLang };
    const list = T.rankedVoices(S.code) || [];
    const tag = (v) => String(v.lang || '').replace('_', '-').toLowerCase();
    for (const r of regions) {
        const want = r.toLowerCase();
        const hit = want.endsWith('-*')
            ? list.find((v) => tag(v).startsWith(want.slice(0, -1)) && !tag(v).endsWith('-es'))
            : list.find((v) => tag(v) === want || tag(v).startsWith(want + '-'));
        if (hit) return { voice: hit, lang: hit.lang };
    }
    return list.length ? { voice: list[0], lang: list[0].lang } : { voice: null, lang: fallbackLang };
}
function checkVoice() {
    const T = window.TTSHelper;
    if (!T || !T.isSupported()) { if (S.say) { S.say = false; renderSheet(); } return; }
    const code = S.code;
    T.ready().then(() => {
        if (code !== S.code) return;
        const ok = T.getVoices(code).length > 0;
        if (ok !== S.say) { S.say = ok; renderSheet(); }
    });
}
function say(text) {
    const T = window.TTSHelper;
    if (!T || !text) return;
    const pick = pickVoice();
    try { T.speak(text, Object.assign({ lang: pick.lang, rate: 0.9 }, pick.voice ? { voice: pick.voice } : {})); } catch (e) { /* озвучка не мешает работе */ }
    funnel('say', { code: S.code });
}
function sayTense(key) {
    if (!S.A) return;
    const [mid, tid] = key.split('.');
    const rows = ((S.A.res.table[mid] || {})[tid]) || [];
    say(rows.filter((r) => r.form != null).map((r) => speechOf(r, S.pronouns)).join('. '));
}

/* ── модель для выдачи ───────────────────────────────────────────────────── */

export function currentModel() {
    if (!S.A || !S.lang) return null;
    const B = S.mode === 'compare' && S.B ? S.B : null;
    return { meta: S.lang.meta, res: S.A.res, resB: B ? B.res : null, pronouns: S.pronouns, langName: langName(S.code), code: S.code };
}

/** Стрелки по панели экранных клавиш: фокус ходит внутри, Tab — одна остановка на панель. */
function onKeysNav(e) {
    if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
    const list = Array.from(e.currentTarget.querySelectorAll('[data-vc-key]'));
    const i = list.indexOf(document.activeElement);
    if (i < 0) return;
    e.preventDefault();
    const rtl = getComputedStyle(e.currentTarget).direction === 'rtl';
    let j = i;
    if (e.key === 'Home') j = 0;
    else if (e.key === 'End') j = list.length - 1;
    else j = (i + ((e.key === 'ArrowRight') !== rtl ? 1 : -1) + list.length) % list.length;
    list.forEach((b, k) => { b.tabIndex = k === j ? 0 : -1; });
    list[j].focus();
}

/* ── запуск ──────────────────────────────────────────────────────────────── */

function init() {
    if (!$('vcApp')) return;
    S.pronouns = loadSetting('pronouns', true) !== false;

    langSeg = seg($('vcLangs'), 'data-vc-lang', (code) => selectLang(code, { byUser: true }));
    modeSeg = seg($('vcModes'), 'data-vc-mode', (m) => setMode(m, true));

    trainer = createTrainer({
        state: S,
        optionsFor: () => optionsFor(S.code),
        names: NAMES,
        say: (text) => say(text),
        canSay: () => S.say,
    });

    bindSearch('A');
    bindSearch('B');
    $('vcQClear').addEventListener('click', () => {
        const q = $('vcQ');
        q.value = '';
        $('vcQClear').hidden = true;
        closeMenu('A');
        showMsg('');
        q.focus();
    });

    // экранные клавиши: в последнее поле ввода, где был человек
    let lastInput = $('vcQ');
    document.addEventListener('focusin', (e) => {
        const el = e.target;
        if (el && el.matches && el.matches('#vcQ, #vcQ2, .vc-tr-in')) lastInput = el;
    });
    $('vcApp').addEventListener('mousedown', (e) => { if (e.target.closest('[data-vc-key]')) e.preventDefault(); });
    for (const id of ['vcKeys', 'vcTrKeys']) $(id).addEventListener('keydown', onKeysNav);

    $('vcApp').addEventListener('click', (e) => {
        const k = e.target.closest('[data-vc-key]');
        if (k) {
            const inTrainer = !!k.closest('#vcTrainer');
            const target = inTrainer ? (lastInput && lastInput.classList.contains('vc-tr-in') ? lastInput : document.querySelector('.vc-tr-in')) : (lastInput && !lastInput.classList.contains('vc-tr-in') ? lastInput : $('vcQ'));
            insertAtCaret(target, k.getAttribute('data-vc-key'));
            return;
        }
        if (e.target.closest('#vcRecentClear')) {
            if (S.code) saveSetting('recent_' + S.code, []);
            renderRecent();
            funnel('recent_clear', { code: S.code });
            $('vcQ').focus();
            return;
        }
        const open = e.target.closest('[data-vc-open]');
        if (open) {
            if (open.closest('#vcRecent')) funnel('recent', { code: S.code });
            const lemma = open.getAttribute('data-vc-open');
            const id = open.getAttribute('data-vc-id');
            const hit = open.getAttribute('data-vc-hit');
            if (id != null && id !== '') openHit({ lemma, id: Number(id), match: hit ? 'form' : 'lemma', matchedForm: hit || null }, 'A', lemma);
            else submit(lemma, 'A');
            return;
        }
        const pair = e.target.closest('[data-vc-pair]');
        if (pair) {
            const [a, b] = pair.getAttribute('data-vc-pair').split('|');
            setMode('compare', true);
            submit(a, 'A', { quiet: true }).then(() => submit(b, 'B', { quiet: true }));
            funnel('pair', { code: S.code });
            return;
        }
        const cmp = e.target.closest('[data-vc-cmp]');
        if (cmp) {
            setMode('compare', true);
            submit(cmp.getAttribute('data-vc-cmp'), 'B', { quiet: true });
            return;
        }
        const sw = e.target.closest('[data-vc-switch]');
        if (sw) {
            selectLang(sw.getAttribute('data-vc-switch'), { byUser: true, verb: sw.getAttribute('data-vc-q') || null });
            return;
        }
        const s1 = e.target.closest('[data-say]');
        if (s1) { say(s1.getAttribute('data-say')); return; }
        const s2 = e.target.closest('[data-say-tense]');
        if (s2) { sayTense(s2.getAttribute('data-say-tense')); return; }
        const act = e.target.closest('[data-vc-act]');
        if (act) {
            const a = act.getAttribute('data-vc-act');
            const model = currentModel();
            // адрес пишется сразу, и окно системы / буфер зовутся в том же нажатии (Safari, QA 14.10)
            if (a === 'share') { shareLink(writeUrl()); return; }
            if (!model) return;
            if (a === 'copy') copySheet(model);
            else if (a === 'pdf') savePdf(act, model);
            else if (a === 'print') printSheet(model);
            else if (a === 'anki') exportAnki(act, model);
        }
    });
    $('vcOpts').addEventListener('change', onOptionsChange);
    $('vcOpts').addEventListener('click', onOptionsClick);

    if (window.TTSHelper && window.TTSHelper.onVoicesChanged) window.TTSHelper.onVoicesChanged(() => checkVoice());

    // начальное состояние: адрес → последний выбор → язык страницы
    const p = new URLSearchParams(location.search);
    const fromUrl = p.get('lang');
    const saved = loadSetting('lang', null);
    const code = CODES.includes(fromUrl) ? fromUrl : CODES.includes(saved) ? saved : (DEFAULT_BY_LOCALE[LOCALE] || 'en');
    const mode = p.get('mode');
    const cmpQ = p.get('cmp');
    if (mode === 'trainer' || mode === 'compare' || cmpQ) {
        S.mode = mode === 'trainer' ? 'trainer' : 'compare';
    }
    modeSeg.set(S.mode);
    $('vcSheet').hidden = S.mode === 'trainer';
    $('vcTrainer').hidden = S.mode !== 'trainer';
    selectLang(code, { verb: p.get('verb') || null, cmp: cmpQ || null, force: true }).then(() => {
        if (S.mode === 'trainer') trainer.show();
        stage('ready', { code: S.code });
    });

    // Опора для сторожей: что показано сейчас.
    window.__vcState = () => ({
        code: S.code, mode: S.mode, busy: S.busy, done: S.done,
        verb: S.A ? unstress(S.A.res.lemma) : null, cmp: S.B ? unstress(S.B.res.lemma) : null,
        guessed: S.A ? !!S.A.res.guessed : null, say: S.say,
        text: currentModel() ? sheetText(currentModel()) : '',
    });
    window.__vcLoader = loader;
    window.__vcVoice = () => { const v = pickVoice(); return { lang: v.lang, voice: v.voice ? v.voice.lang : null }; };
    window.__vcOptions = () => optionsFor(S.code);
    window.__vcTrainerCard = () => {
        const c = trainer.current();
        return c ? { lemma: c.res.lemma, tense: c.tense, rows: c.rows.map((r) => ({ person: r.person, form: r.form, alt: r.alt || [] })) } : null;
    };
}

if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
else init();

export { lineOf };
